import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/user';
import { isInterviewerRole } from '../utils/role';
import { profileService } from '../services/profile';
import { authService } from '../services/auth';
import { SEO } from '../components/SEO';
import { ConfirmDialog } from '../components/ui';

const getTranslation = (t: (key: string) => string, key: string, fallback: string): string => {
  const translation = t(key);
  return translation === key ? fallback : translation;
};

const HERO_CTA_CLASSES =
  'inline-flex w-full items-center justify-center rounded-stitch-xl bg-stitch-primary px-8 py-4 text-lg font-bold text-white shadow-xl shadow-stitch-primary/20 transition-all hover:bg-stitch-primary-hover active:scale-[0.98] sm:w-auto';

const INVERSE_CTA_CLASSES =
  'inline-flex w-full items-center justify-center rounded-stitch-xl bg-white px-6 py-3.5 text-base font-bold text-stitch-primary shadow-xl transition-all hover:bg-white/90 active:scale-[0.98] sm:w-auto sm:px-8 sm:py-4 sm:text-lg';

/**
 * The one primary action on this page. Guests go to registration with the
 * interviewer role preselected; signed-in non-interviewers get the existing
 * role-switch flow (confirmed via dialog); interviewers manage availability.
 */
function ApplyCta({
  variant,
  onSwitchRequest,
}: {
  variant: 'hero' | 'inverse';
  onSwitchRequest: () => void;
}) {
  const { t } = useTranslation();
  const { isAuthenticated, userRole } = useAuth();
  const classes = variant === 'hero' ? HERO_CTA_CLASSES : INVERSE_CTA_CLASSES;
  const arrow = <span className="material-symbols-outlined ml-2 text-lg">arrow_forward</span>;

  if (isAuthenticated && isInterviewerRole(userRole)) {
    return (
      <Link to="/time-slots" className={classes}>
        {getTranslation(t, 'becomeInterviewer.ctaManage', 'Manage your availability')}
        {arrow}
      </Link>
    );
  }

  if (isAuthenticated) {
    return (
      <button type="button" onClick={onSwitchRequest} className={classes}>
        {getTranslation(t, 'becomeInterviewer.cta', 'Apply as an interviewer')}
        {arrow}
      </button>
    );
  }

  return (
    <Link to="/register?role=interviewer" className={classes}>
      {getTranslation(t, 'becomeInterviewer.cta', 'Apply as an interviewer')}
      {arrow}
    </Link>
  );
}

export function BecomeInterviewer() {
  const { t } = useTranslation();
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);

  const handleSwitchToInterviewer = async () => {
    setSwitching(true);
    setSwitchError(null);
    try {
      const response = await profileService.updateUserRole(UserRole.Interviewer);
      authService.setToken(response.token, response.userId, response.roleId);
      checkAuth();
      setConfirmOpen(false);
      navigate('/profile/setup');
    } catch (err) {
      setSwitchError(
        err instanceof Error && err.message
          ? err.message
          : getTranslation(t, 'becomeInterviewer.switchFailed', 'Failed to switch your role. Please try again.'),
      );
    } finally {
      setSwitching(false);
    }
  };

  const audience = [
    {
      icon: 'workspace_premium',
      title: getTranslation(t, 'becomeInterviewer.who1Title', 'Senior specialists'),
      desc: getTranslation(
        t,
        'becomeInterviewer.who1Desc',
        'Deep hands-on expertise in your field — engineering, finance, marketing, medicine, anything.',
      ),
    },
    {
      icon: 'groups',
      title: getTranslation(t, 'becomeInterviewer.who2Title', 'Team leads'),
      desc: getTranslation(
        t,
        'becomeInterviewer.who2Desc',
        'You have interviewed candidates for your own team and know what a strong answer sounds like.',
      ),
    },
    {
      icon: 'supervisor_account',
      title: getTranslation(t, 'becomeInterviewer.who3Title', 'Hiring managers'),
      desc: getTranslation(
        t,
        'becomeInterviewer.who3Desc',
        'You have owned hiring decisions and can coach candidates on what really matters.',
      ),
    },
  ];

  const perks = [
    {
      icon: 'military_tech',
      chipClass: 'bg-stitch-primary-soft text-stitch-primary',
      title: getTranslation(t, 'becomeInterviewer.perk1Title', 'Founding Interviewer status'),
      desc: getTranslation(
        t,
        'becomeInterviewer.perk1Desc',
        'Be recognized from day one as one of the experts who helped start InterviewR.',
      ),
    },
    {
      icon: 'badge',
      chipClass: 'bg-stitch-accent-soft text-stitch-accent',
      title: getTranslation(t, 'becomeInterviewer.perk2Title', 'A public expert profile'),
      desc: getTranslation(
        t,
        'becomeInterviewer.perk2Desc',
        'Showcase your experience, specialties, and languages on a profile you can share anywhere.',
      ),
    },
    {
      icon: 'tips_and_updates',
      chipClass: 'bg-stitch-success-soft text-stitch-success',
      title: getTranslation(t, 'becomeInterviewer.perk3Title', 'Direct influence on the roadmap'),
      desc: getTranslation(
        t,
        'becomeInterviewer.perk3Desc',
        'Founding feedback shapes what we build next — tell us what interviewers actually need.',
      ),
    },
    {
      icon: 'payments',
      chipClass: 'bg-stitch-warning-soft text-stitch-warning',
      title: getTranslation(t, 'becomeInterviewer.perk4Title', 'First in line for paid sessions'),
      desc: getTranslation(
        t,
        'becomeInterviewer.perk4Desc',
        'When paid mock interviews open on InterviewR, founding interviewers get access first.',
      ),
    },
  ];

  const steps = [
    {
      title: getTranslation(t, 'becomeInterviewer.step1Title', 'Apply in 5 minutes'),
      desc: getTranslation(
        t,
        'becomeInterviewer.step1Desc',
        'Create your account as an interviewer and tell us about your experience.',
      ),
    },
    {
      title: getTranslation(t, 'becomeInterviewer.step2Title', 'We review and verify your experience'),
      desc: getTranslation(
        t,
        'becomeInterviewer.step2Desc',
        'A quick, human review of your background — credibility matters to candidates.',
      ),
    },
    {
      title: getTranslation(t, 'becomeInterviewer.step3Title', 'Run mock interviews on your own schedule'),
      desc: getTranslation(
        t,
        'becomeInterviewer.step3Desc',
        'Open time slots when it suits you. Candidates book, you interview.',
      ),
    },
  ];

  return (
    <>
      <SEO
        title="Become an Interviewer | InterviewR"
        description="Apply to become a Founding Interviewer on InterviewR. Build your public expert profile, shape the product roadmap, and run mock interviews on your own schedule."
        url={typeof window !== 'undefined' ? window.location.href : 'https://interview-r.com/become-interviewer'}
      />

      {/* Landing-only display font + load reveal, same as Home. Respects reduced-motion. */}
      <style>{`
        .hr-display { font-family: 'Bricolage Grotesque', ui-sans-serif, system-ui, -apple-system, sans-serif; letter-spacing: -0.02em; }
        @keyframes hr-rise { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }
        .hr-rise { opacity: 0; animation: hr-rise 0.7s cubic-bezier(0.33, 1, 0.68, 1) forwards; }
        @media (prefers-reduced-motion: reduce) {
          .hr-rise { animation: none; opacity: 1; }
        }
      `}</style>

      <div className="min-h-screen bg-stitch-canvas">
        {/* ── Hero ───────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden pt-12 pb-20 sm:pt-16 sm:pb-24 lg:pt-20 lg:pb-28">
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -top-40 right-[-10%] h-[40rem] w-[40rem] rounded-full bg-stitch-accent/15 blur-[130px]" />
            <div className="absolute left-[-15%] top-32 h-[34rem] w-[34rem] rounded-full bg-stitch-primary/15 blur-[130px]" />
            <div className="absolute inset-0 opacity-[0.04] [background-image:radial-gradient(circle_at_1px_1px,rgb(15_23_42)_1px,transparent_0)] [background-size:22px_22px]" />
          </div>

          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <span
                className="hr-rise mb-6 inline-flex max-w-full items-center rounded-full border border-stitch-accent/20 bg-stitch-accent-soft px-3 py-1 text-xs font-semibold uppercase tracking-wider text-stitch-accent"
                style={{ animationDelay: '0ms' }}
              >
                <span
                  className="material-symbols-outlined mr-1 text-sm"
                  style={{ fontVariationSettings: "'FILL' 1" }}
                >
                  rocket_launch
                </span>
                {getTranslation(t, 'becomeInterviewer.badge', 'Founding Interviewer Program')}
              </span>

              <h1
                className="hr-display hr-rise mb-6 text-4xl font-extrabold leading-[1.05] text-stitch-fg sm:text-5xl md:text-6xl"
                style={{ animationDelay: '80ms' }}
              >
                {getTranslation(t, 'becomeInterviewer.heroTitleLead', 'Become a')}{' '}
                <span className="bg-gradient-to-r from-stitch-primary to-stitch-accent bg-clip-text text-transparent">
                  {getTranslation(t, 'becomeInterviewer.heroTitleAccent', 'Founding Interviewer')}
                </span>
              </h1>

              <p
                className="hr-rise mx-auto mb-9 max-w-xl text-lg leading-relaxed text-stitch-muted sm:text-xl"
                style={{ animationDelay: '160ms' }}
              >
                {getTranslation(
                  t,
                  'becomeInterviewer.heroSubtitle',
                  'Share your expertise, build your public expert profile, and shape InterviewR from day one.',
                )}
              </p>

              <div
                className="hr-rise flex flex-col items-center justify-center gap-4 sm:flex-row"
                style={{ animationDelay: '240ms' }}
              >
                <ApplyCta variant="hero" onSwitchRequest={() => setConfirmOpen(true)} />
                <a
                  href="#how-it-works"
                  className="inline-flex items-center text-base font-semibold text-stitch-fg-soft transition-colors hover:text-stitch-primary"
                >
                  {getTranslation(t, 'becomeInterviewer.ctaSecondary', 'See how it works')}
                  <span className="material-symbols-outlined ml-1 text-lg">arrow_forward</span>
                </a>
              </div>

              <p
                className="hr-rise mt-4 flex items-center justify-center gap-1.5 text-sm text-stitch-muted"
                style={{ animationDelay: '300ms' }}
              >
                <span className="material-symbols-outlined text-base text-stitch-success">check_circle</span>
                {getTranslation(
                  t,
                  'becomeInterviewer.ctaTrust',
                  'Free to join — the application takes about 5 minutes.',
                )}
              </p>
            </div>
          </div>
        </section>

        {/* ── Who we're looking for (clarity band) ───────────────────────── */}
        <section className="border-y border-stitch-border bg-stitch-surface py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <span className="text-sm font-semibold uppercase tracking-widest text-stitch-primary">
                {getTranslation(t, 'becomeInterviewer.whoKicker', "Who we're looking for")}
              </span>
              <p className="hr-display mt-3 text-2xl font-bold leading-snug text-stitch-fg sm:text-3xl">
                {getTranslation(
                  t,
                  'becomeInterviewer.whoTitle',
                  'Senior specialists, team leads, and managers from any industry who have sat on the hiring side of interviews.',
                )}
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-3">
              {audience.map((a) => (
                <div
                  key={a.icon}
                  className="flex items-start gap-4 rounded-stitch-xl border border-stitch-border bg-stitch-canvas p-5"
                >
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-stitch-lg bg-stitch-primary-soft text-stitch-primary">
                    <span className="material-symbols-outlined">{a.icon}</span>
                  </div>
                  <div>
                    <p className="font-bold text-stitch-fg">{a.title}</p>
                    <p className="mt-0.5 text-sm text-stitch-muted">{a.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── What Founding Interviewers get ─────────────────────────────── */}
        <section className="bg-stitch-canvas py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-14 max-w-2xl">
              <h2 className="hr-display mb-4 text-3xl font-bold text-stitch-fg md:text-4xl">
                {getTranslation(t, 'becomeInterviewer.perksTitle', 'What Founding Interviewers get')}
              </h2>
              <p className="text-lg text-stitch-muted">
                {getTranslation(
                  t,
                  'becomeInterviewer.perksSubtitle',
                  'Four concrete things you get for helping us build this from the start.',
                )}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {perks.map((perk) => (
                <div
                  key={perk.icon}
                  className="group rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-8 shadow-stitch-card transition-all duration-300 hover:border-stitch-primary/20 hover:shadow-stitch-pop"
                >
                  <div
                    className={`mb-6 flex h-14 w-14 items-center justify-center rounded-stitch-lg transition-transform group-hover:scale-110 ${perk.chipClass}`}
                  >
                    <span className="material-symbols-outlined text-3xl">{perk.icon}</span>
                  </div>
                  <h3 className="mb-3 text-xl font-bold text-stitch-fg">{perk.title}</h3>
                  <p className="leading-relaxed text-stitch-muted">{perk.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── How it works — dark band ───────────────────────────────────── */}
        <section
          id="how-it-works"
          className="scroll-mt-24 bg-stitch-footer-bg py-20 text-stitch-footer-fg sm:py-24"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-16 max-w-xl text-left">
              <h2 className="hr-display mb-4 text-3xl font-bold md:text-4xl">
                {getTranslation(t, 'becomeInterviewer.howTitle', 'How it works')}
              </h2>
              <p className="text-lg text-stitch-footer-muted">
                {getTranslation(
                  t,
                  'becomeInterviewer.howSubtitle',
                  'From application to your first mock interview — three steps.',
                )}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {steps.map((step, i) => (
                <div
                  key={step.title}
                  className="rounded-2xl border border-stitch-border/50 bg-stitch-overlay/40 p-8 backdrop-blur-sm"
                >
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-stitch-primary/20 text-lg font-bold">
                    {i + 1}
                  </div>
                  <h3 className="mb-2 text-xl font-bold">{step.title}</h3>
                  <p className="leading-relaxed text-stitch-footer-muted">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Closing CTA ────────────────────────────────────────────────── */}
        <section className="bg-stitch-canvas px-4 py-20 sm:py-24">
          <div className="relative mx-auto max-w-5xl overflow-hidden rounded-stitch-2xl bg-gradient-to-br from-stitch-primary via-stitch-primary-hover to-stitch-accent px-6 py-14 text-center shadow-stitch-pop sm:px-12">
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-12 h-56 w-56 rounded-full bg-stitch-accent/40 blur-3xl" />
            <div className="relative">
              <h2 className="hr-display mx-auto max-w-2xl text-3xl font-extrabold text-white sm:text-4xl">
                {getTranslation(
                  t,
                  'becomeInterviewer.closingTitle',
                  'Every senior professional was once the nervous candidate.',
                )}
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-lg text-white/85">
                {getTranslation(
                  t,
                  'becomeInterviewer.closingSubtitle',
                  "Be the interviewer you wish you'd had.",
                )}
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <ApplyCta variant="inverse" onSwitchRequest={() => setConfirmOpen(true)} />
              </div>
            </div>
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          if (!switching) {
            setConfirmOpen(false);
            setSwitchError(null);
          }
        }}
        onConfirm={handleSwitchToInterviewer}
        title={getTranslation(t, 'becomeInterviewer.switchTitle', 'Switch to an interviewer account?')}
        description={
          <>
            <span className="block">
              {getTranslation(
                t,
                'becomeInterviewer.switchDescription',
                'We will set your InterviewR role to Interviewer. Next, you will complete your expert profile — your specialty, company, and experience.',
              )}
            </span>
            {switchError && (
              <span className="mt-2 block text-stitch-danger" role="alert">
                {switchError}
              </span>
            )}
          </>
        }
        confirmLabel={getTranslation(t, 'becomeInterviewer.switchConfirm', 'Switch and continue')}
        cancelLabel={getTranslation(t, 'common.cancel', 'Cancel')}
        loading={switching}
      />
    </>
  );
}
